import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartTooltipShell } from "../panel";
import { DEVELOPMENT_AGES, ULTIMATE_AGE } from "../../../pages/PortfolioPerformance/lib/api/loss-calibration";
import { cohortColor } from "../../../pages/PortfolioPerformance/lib/api/lifetime-loss-projections";
import type { LossDevelopmentPoint } from "../../../pages/PortfolioPerformance/lib/api/loss-calibration";

const AXIS_COLOR = "var(--neutral-inactive)";

/** One distinct colour per vintage, from the palette every Portfolio Performance multi-line chart shares. */
export function colorFor(vintage: number, allVintages: number[]) {
  const index = allVintages.indexOf(vintage);
  return cohortColor(index < 0 ? 0 : index);
}

export const CHART_DASH = "1 3";
export const PROJECTED_DASH = "4 7";

function ageLabel(age: number) {
  return age >= ULTIMATE_AGE ? "ultimate" : String(age);
}

function DevelopmentTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number | null; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const seen = new Set<string>();
  const entries = payload.filter(
    (entry): entry is { name: string; value: number; color: string } => {
      if (entry.value == null || seen.has(entry.name)) return false;
      seen.add(entry.name);
      return true;
    },
  );
  if (!entries.length) return null;
  return (
    <ChartTooltipShell>
      <p className="font-semibold text-foreground">Age {label}</p>
      <div className="mt-1 space-y-0.5">
        {entries.map((entry) => (
          <p
            key={entry.name}
            className="flex items-center gap-2 text-muted-foreground"
          >
            <span className="size-2" style={{ backgroundColor: entry.color }} aria-hidden />
            {entry.name}
            <span className="ml-auto tabular-nums text-foreground">
              {entry.value.toFixed(2)}%
            </span>
          </p>
        ))}
      </div>
    </ChartTooltipShell>
  );
}

export function LossDevelopmentChart({
  points,
  allVintages,
  height = 340,
  showProjected = true,
}: {
  points: LossDevelopmentPoint[];
  allVintages?: number[];
  height?: number;
  showProjected?: boolean;
}) {
  const vintages = Array.from(new Set(points.map((p) => p.vintage))).sort((a, b) => a - b);
  const palette = allVintages?.length ? allVintages : vintages;
  const newest = vintages[vintages.length - 1];
  const widthFor = (vintage: number) => (vintage === newest ? 2.75 : 1.75);

  const data = DEVELOPMENT_AGES.map((age) => {
    const row: Record<string, number | string | null> = { age: ageLabel(age) };
    for (const vintage of vintages) {
      const point = points.find((p) => p.vintage === vintage && p.ageMonths === age);
      const value = point?.chartLossPct ?? null;
      row[`v${vintage}`] = point && !point.isProjected ? value : null;
      row[`v${vintage}c`] = point && point.isProjected ? value : null;
      row[`v${vintage}p`] = point?.projectedLossPct ?? null;
    }
    return row;
  });

  for (const vintage of vintages) {
    const lastRealized = points
      .filter((p) => p.vintage === vintage && !p.isProjected && p.chartLossPct != null)
      .sort((a, b) => b.ageMonths - a.ageMonths)[0];
    if (!lastRealized) continue;
    const index = DEVELOPMENT_AGES.indexOf(
      lastRealized.ageMonths as (typeof DEVELOPMENT_AGES)[number],
    );
    if (index >= 0 && data[index]) {
      data[index]![`v${vintage}c`] = lastRealized.chartLossPct;
    }
  }

  return (
    <div className="w-full">
      <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 24, left: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="age"
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            stroke={AXIS_COLOR}
            tickLine={false}
            label={{
              value: "Age (months since origination)",
              position: "insideBottom",
              offset: -14,
              fill: AXIS_COLOR,
              fontSize: 11,
            }}
          />
          <YAxis
            tickFormatter={(value: number) => `${value.toFixed(1)}%`}
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            stroke={AXIS_COLOR}
            width={56}
          />
          <Tooltip
            content={<DevelopmentTooltip />}
            contentStyle={{
              background: "var(--popover)",
              border: "1px solid var(--border)",
              borderRadius: 0,
            }}
          />
          {vintages.map((vintage) => (
            <Line
              key={`v${vintage}`}
              type="monotone"
              dataKey={`v${vintage}`}
              name={`${vintage} actual`}
              stroke={colorFor(vintage, palette)}
              strokeWidth={widthFor(vintage)}
              dot={{ r: 2.5 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
          {vintages.map((vintage) => (
            <Line
              key={`v${vintage}c`}
              type="monotone"
              dataKey={`v${vintage}c`}
              name={`${vintage} actual`}
              stroke={colorFor(vintage, palette)}
              strokeWidth={widthFor(vintage)}
              strokeDasharray={CHART_DASH}
              strokeLinecap="round"
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
          {showProjected &&
            vintages.map((vintage) => (
              <Line
                key={`v${vintage}p`}
                type="monotone"
                dataKey={`v${vintage}p`}
                name={`${vintage} projected`}
                stroke={colorFor(vintage, palette)}
                strokeWidth={widthFor(vintage) + 0.75}
                strokeDasharray={PROJECTED_DASH}
                strokeLinecap="round"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
        </ComposedChart>
      </ResponsiveContainer>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        {vintages.map((vintage) => (
          <span key={vintage} className="flex items-center gap-1.5">
            <span
              className="inline-block h-0.5 w-4"
              style={{ backgroundColor: colorFor(vintage, palette) }}
              aria-hidden
            />
            {vintage}
          </span>
        ))}
        <span className="ml-auto">
          {showProjected ? "Solid = actual · dotted = projected" : "Solid = actual"}
        </span>
      </div>
    </div>
  );
}
